import { hasPermission } from '@ctrl/db';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Gift } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { SelectField, TextField } from '@/components/form-fields';
import { useDetailTitle } from '@/components/page-header';
import {
  EmptyState,
  ListRow,
  ListShell,
  ReadOnlyNotice,
  SectionCard,
} from '@/components/settings/settings-kit';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth';
import {
  adjustGiftCard,
  type GiftCardAdjustKind,
  listGiftCards,
  listGiftCardTransactions,
} from '@/lib/retention';

/**
 * One gift card: its balance, every movement against it, and a way to put
 * money back on or take it off.
 *
 * Adjustments go through `gift_card_adjust`, which writes the transaction
 * and the new balance in one statement -- a balance edited directly would
 * leave the ledger disagreeing with the card.
 */
export const Route = createFileRoute('/gift-cards/$cardId')({
  component: GiftCardDetailPage,
});

const usd = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

function GiftCardDetailPage() {
  const { cardId } = useParams({ from: '/gift-cards/$cardId' });
  const { activeAccountId, claims } = useAuth();
  const accountId = activeAccountId ?? '';
  const queryClient = useQueryClient();

  const canManage = accountId ? hasPermission(claims, accountId, 'discounts.manage') : false;

  const [amount, setAmount] = useState('10.00');
  const [kind, setKind] = useState<GiftCardAdjustKind>('adjusted');

  const cards = useQuery({
    queryKey: ['gift-cards', accountId],
    queryFn: () => listGiftCards(accountId),
    enabled: Boolean(accountId),
  });

  const transactions = useQuery({
    queryKey: ['gift-card-transactions', accountId, cardId],
    queryFn: () => listGiftCardTransactions(accountId, cardId),
    enabled: Boolean(accountId),
  });

  const card = cards.data?.find((row) => row.id === cardId);

  useDetailTitle(card ? `Card ••••${card.last4 ?? ''}` : `Card ${cardId.slice(0, 8)}`);

  const adjust = useMutation({
    mutationFn: () => {
      const cents = Math.round(Number(amount) * 100);

      if (!Number.isFinite(cents) || cents === 0) throw new Error('Enter an amount.');

      return adjustGiftCard(accountId, { giftCardId: cardId, amountCents: cents, kind });
    },
    onSuccess: () => {
      toast.success('Balance adjusted.');
      queryClient.invalidateQueries({ queryKey: ['gift-cards', accountId] });
      queryClient.invalidateQueries({ queryKey: ['gift-card-transactions', accountId, cardId] });
    },
    onError: (error: Error) => toast.error(error.message.replace(/^[a-z_]+:\s*/i, '')),
  });

  if (cards.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!card) {
    return (
      <div className="text-muted-foreground flex flex-col items-center gap-3 py-24 text-center">
        <Gift className="size-8" />
        <p className="text-foreground text-sm font-medium">No such card</p>
        <Button asChild variant="outline" size="sm">
          <Link to="/gift-cards">
            <ArrowLeft className="size-4" />
            Back to gift cards
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="font-mono text-2xl font-semibold tracking-tight">
          •••• {card.last4 ?? '????'}
        </h1>
        <p className="text-muted-foreground text-sm">
          {usd(card.balanceCents)} of {usd(card.initialBalanceCents)} remaining
          {card.recipientEmail ? ` · for ${card.recipientEmail}` : ''}
        </p>
      </div>

      {!canManage && <ReadOnlyNotice permission="discounts.manage" />}

      {canManage && (
        <SectionCard
          title="Adjust the balance"
          description="A positive amount adds, a negative one takes off. Every movement is recorded below."
        >
          <div className="flex flex-wrap items-end gap-3">
            <TextField
              id="adjust-amount"
              label="Amount"
              className="w-40"
              value={amount}
              onChange={setAmount}
            />
            <SelectField
              id="adjust-kind"
              label="Reason"
              className="w-48"
              value={kind}
              options={[
                { value: 'adjusted', label: 'Adjustment' },
                { value: 'refunded', label: 'Refund to card' },
                { value: 'expired', label: 'Expired' },
              ]}
              onChange={(value) => setKind(value as GiftCardAdjustKind)}
            />
            <Button type="button" onClick={() => adjust.mutate()} disabled={adjust.isPending}>
              {adjust.isPending ? 'Saving…' : 'Apply'}
            </Button>
          </div>
        </SectionCard>
      )}

      <SectionCard title="History" description="Every load, spend and adjustment, newest first.">
        {transactions.data?.length ? (
          <ListShell>
            {transactions.data.map((entry) => (
              <ListRow
                key={entry.id}
                title={`${entry.amountCents >= 0 ? '+' : ''}${usd(entry.amountCents)} · ${entry.kind.replace(/_/g, ' ')}`}
                subtitle={`Balance ${usd(entry.balanceAfter)}${
                  entry.actorName ? ` · ${entry.actorName}` : ''
                } · ${new Intl.DateTimeFormat('en-US', {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                }).format(new Date(entry.createdAt))}`}
              />
            ))}
          </ListShell>
        ) : (
          <EmptyState>Nothing has moved on this card yet.</EmptyState>
        )}
      </SectionCard>
    </div>
  );
}
